import { useState, useEffect, useCallback, useRef } from 'react';
import { taxReportApi, formatCurrency } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileBarChart, Download, AlertCircle, TrendingUp, TrendingDown,
  Building2, Calendar, FileText, Loader2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import AppLayout from '@/components/AppLayout';
import PropertyFilter from '@/components/PropertyFilter';

// Generate tax year options (current and previous 4 years)
const generateTaxYearOptions = () => {
  const today = new Date();
  let currentTaxYearStart = today.getFullYear();
  
  // UK tax year starts April 6
  if (today.getMonth() < 3 || (today.getMonth() === 3 && today.getDate() < 6)) {
    currentTaxYearStart -= 1;
  }
  
  const options = [];
  for (let i = 0; i < 5; i++) {
    const startYear = currentTaxYearStart - i;
    const endYear = startYear + 1;
    options.push({
      value: `${startYear}-${String(endYear).slice(2)}`,
      label: `${startYear}/${endYear}`
    });
  }
  return options;
};

const TAX_YEAR_OPTIONS = generateTaxYearOptions();

const QUARTER_OPTIONS = [
  { value: 1, label: 'Q1 (6 Apr - 5 Jul)' },
  { value: 2, label: 'Q2 (6 Jul - 5 Oct)' },
  { value: 3, label: 'Q3 (6 Oct - 5 Jan)' },
  { value: 4, label: 'Q4 (6 Jan - 5 Apr)' },
];

// Category display configuration
const EXPENSE_CATEGORIES = [
  { key: 'finance_mortgage', label: 'Finance/Mortgage', color: 'text-purple-600' },
  { key: 'letting_agent_fees', label: 'Letting Agent Fees', color: 'text-blue-600' },
  { key: 'insurance', label: 'Insurance', color: 'text-cyan-600' },
  { key: 'maintenance_repairs', label: 'Maintenance/Repairs', color: 'text-orange-600' },
  { key: 'council_tax', label: 'Council Tax', color: 'text-rose-600' },
  { key: 'other_expenses', label: 'Other', color: 'text-slate-600' },
];

export default function ReportsPage() {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [taxYear, setTaxYear] = useState(TAX_YEAR_OPTIONS[0].value);
  const [periodType, setPeriodType] = useState('full_year');
  const [quarter, setQuarter] = useState(1);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  const fetchReport = useCallback(async () => {
    if (selectedPropertyIds.length === 0) {
      setReport(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await taxReportApi.get(
        taxYear,
        periodType,
        periodType === 'quarter' ? quarter : undefined,
        selectedPropertyIds
      );
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [taxYear, periodType, quarter, selectedPropertyIds]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // CSV Export - with proper escaping
  const exportCSV = () => {
    if (!report) return;
    setExporting(true);

    try {
      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        // Escape spreadsheet formula injection (prevent =, +, -, @, tab, carriage return from being interpreted)
        const sanitized = str.replace(/^[=+\-@\t\r]/, "'$&");
        // If the value contains commas, quotes, or newlines, wrap in quotes and escape internal quotes
        if (sanitized.includes(',') || sanitized.includes('"') || sanitized.includes('\n')) {
          return `"${sanitized.replace(/"/g, '""')}"`;
        }
        return sanitized;
      };

      const rows = [];
      
      // Header
      rows.push([
        escapeCSV('UK Tax Report'),
        escapeCSV(`Tax Year: ${report.tax_year}`),
        escapeCSV(`Period: ${periodType === 'quarter' ? `Q${quarter}` : 'Full Year'}`),
        escapeCSV(`Generated: ${new Date().toLocaleDateString('en-GB')}`)
      ].join(','));
      rows.push('');
      
      // Column headers
      rows.push([
        'Property',
        'Rental Income',
        'Finance/Mortgage',
        'Letting Agent Fees',
        'Insurance',
        'Maintenance/Repairs',
        'Council Tax',
        'Other Expenses',
        'Total Expenses',
        'Net Profit',
        'Data Complete'
      ].join(','));
      
      // Property rows
      report.properties.forEach(prop => {
        rows.push([
          escapeCSV(prop.property_address),
          prop.rental_income.toFixed(2),
          prop.finance_mortgage.toFixed(2),
          prop.letting_agent_fees.toFixed(2),
          prop.insurance.toFixed(2),
          prop.maintenance_repairs.toFixed(2),
          prop.council_tax.toFixed(2),
          prop.other_expenses.toFixed(2),
          prop.total_expenses.toFixed(2),
          prop.net_profit.toFixed(2),
          prop.has_data_gaps ? 'No' : 'Yes'
        ].join(','));
      });
      
      // Portfolio totals
      rows.push('');
      rows.push([
        'PORTFOLIO TOTAL',
        report.portfolio_totals.rental_income.toFixed(2),
        report.portfolio_totals.finance_mortgage.toFixed(2),
        report.portfolio_totals.letting_agent_fees.toFixed(2),
        report.portfolio_totals.insurance.toFixed(2),
        report.portfolio_totals.maintenance_repairs.toFixed(2),
        report.portfolio_totals.council_tax.toFixed(2),
        report.portfolio_totals.other_expenses.toFixed(2),
        report.portfolio_totals.total_expenses.toFixed(2),
        report.portfolio_totals.net_profit.toFixed(2),
        ''
      ].join(','));
      
      rows.push('');
      rows.push(escapeCSV(report.disclaimer));
      
      // Create and download file
      const csvContent = rows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `tax-report-${report.tax_year}-${periodType === 'quarter' ? `Q${quarter}` : 'full'}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  // PDF Export using print-to-PDF styling
  const exportPDF = () => {
    if (!report) return;
    setExporting(true);

    try {
      // Helper function to escape HTML entities
      const escapeHtml = (text) => {
        if (text === null || text === undefined) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      };

      // Create a new window with styled content for printing
      const printWindow = window.open('', '_blank');
      
      const periodLabel = periodType === 'quarter' 
        ? `Quarter ${quarter} (${QUARTER_OPTIONS.find(q => q.value === quarter)?.label.split(' ').slice(1).join(' ')})`
        : 'Full Tax Year';
      
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Tax Report - ${report.tax_year}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              padding: 40px;
              color: #1e293b;
              line-height: 1.5;
            }
            .header { margin-bottom: 32px; }
            .logo { font-size: 24px; font-weight: 700; margin-bottom: 8px; }
            .logo span { color: #2563eb; }
            .title { font-size: 20px; font-weight: 600; color: #0f172a; }
            .subtitle { font-size: 14px; color: #64748b; margin-top: 4px; }
            .meta { 
              display: flex; 
              gap: 24px; 
              margin-top: 16px; 
              padding: 12px 16px;
              background: #f8fafc;
              border-radius: 8px;
            }
            .meta-item { font-size: 13px; }
            .meta-label { color: #64748b; }
            .meta-value { font-weight: 600; color: #0f172a; }
            .disclaimer {
              background: #fef3c7;
              border: 1px solid #fcd34d;
              border-radius: 8px;
              padding: 12px 16px;
              margin: 24px 0;
              font-size: 12px;
              color: #92400e;
            }
            .warning-banner {
              background: #fff7ed;
              border: 1px solid #fed7aa;
              border-radius: 8px;
              padding: 12px 16px;
              margin: 16px 0;
              font-size: 13px;
              color: #c2410c;
            }
            .section { margin-top: 24px; }
            .section-title { font-size: 16px; font-weight: 600; margin-bottom: 12px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { 
              text-align: left; 
              padding: 10px 8px; 
              background: #f1f5f9; 
              font-weight: 600;
              border-bottom: 2px solid #e2e8f0;
            }
            td { 
              padding: 10px 8px; 
              border-bottom: 1px solid #e2e8f0;
            }
            .text-right { text-align: right; }
            .income { color: #059669; }
            .expense { color: #dc2626; }
            .profit { font-weight: 600; }
            .profit.positive { color: #059669; }
            .profit.negative { color: #dc2626; }
            .total-row { background: #f8fafc; font-weight: 600; }
            .total-row td { border-top: 2px solid #e2e8f0; }
            .gap-warning { 
              display: inline-block;
              background: #fef3c7;
              color: #92400e;
              padding: 2px 6px;
              border-radius: 4px;
              font-size: 10px;
              margin-left: 8px;
            }
            .footer { 
              margin-top: 32px; 
              padding-top: 16px; 
              border-top: 1px solid #e2e8f0;
              font-size: 11px;
              color: #94a3b8;
            }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">Portfolio <span>Pal</span></div>
            <div class="title">UK Tax Report - ${report.tax_year}</div>
            <div class="subtitle">Self Assessment / Making Tax Digital Record Summary</div>
            <div class="meta">
              <div class="meta-item">
                <span class="meta-label">Period: </span>
                <span class="meta-value">${periodLabel}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">From: </span>
                <span class="meta-value">${new Date(report.period_start).toLocaleDateString('en-GB')}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">To: </span>
                <span class="meta-value">${new Date(report.period_end).toLocaleDateString('en-GB')}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">Generated: </span>
                <span class="meta-value">${new Date().toLocaleDateString('en-GB')}</span>
              </div>
            </div>
          </div>
          
          <div class="disclaimer">
            <strong>Important:</strong> ${escapeHtml(report.disclaimer)}
          </div>
          
          ${report.has_any_data_gaps ? `
            <div class="warning-banner">
              <strong>Data Completeness Warning:</strong> Some properties have missing data for one or more months in this period. 
              Totals may be incomplete. Review the "Missing Months" column below.
            </div>
          ` : ''}
          
          <div class="section">
            <div class="section-title">Property Breakdown</div>
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th class="text-right">Rental Income</th>
                  <th class="text-right">Total Expenses</th>
                  <th class="text-right">Net Profit</th>
                  <th>Missing Months</th>
                </tr>
              </thead>
              <tbody>
                ${report.properties.map(prop => `
                  <tr>
                    <td>${escapeHtml(prop.property_address)}</td>
                    <td class="text-right income">£${prop.rental_income.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                    <td class="text-right expense">£${prop.total_expenses.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                    <td class="text-right profit ${prop.net_profit >= 0 ? 'positive' : 'negative'}">
                      £${prop.net_profit.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </td>
                    <td>${prop.has_data_gaps ? escapeHtml(prop.missing_months.join(', ')) : 'Complete'}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td><strong>PORTFOLIO TOTAL</strong></td>
                  <td class="text-right income">£${report.portfolio_totals.rental_income.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                  <td class="text-right expense">£${report.portfolio_totals.total_expenses.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                  <td class="text-right profit ${report.portfolio_totals.net_profit >= 0 ? 'positive' : 'negative'}">
                    £${report.portfolio_totals.net_profit.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">Expense Category Breakdown (Portfolio Total)</div>
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Finance/Mortgage</td><td class="text-right">£${report.portfolio_totals.finance_mortgage.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td></tr>
                <tr><td>Letting Agent Fees</td><td class="text-right">£${report.portfolio_totals.letting_agent_fees.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td></tr>
                <tr><td>Insurance</td><td class="text-right">£${report.portfolio_totals.insurance.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td></tr>
                <tr><td>Maintenance/Repairs</td><td class="text-right">£${report.portfolio_totals.maintenance_repairs.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td></tr>
                <tr><td>Council Tax</td><td class="text-right">£${report.portfolio_totals.council_tax.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td></tr>
                <tr><td>Other Expenses</td><td class="text-right">£${report.portfolio_totals.other_expenses.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td></tr>
                <tr class="total-row">
                  <td><strong>Total Expenses</strong></td>
                  <td class="text-right"><strong>£${report.portfolio_totals.total_expenses.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="footer">
            Generated by Portfolio Pal | For Self Assessment / MTD record-keeping purposes only
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(html);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
      };
      
      toast.success('PDF export opened - use your browser\'s print dialog to save as PDF');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-8" data-testid="reports-page" ref={reportRef}>
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-ink font-display tracking-tight">Tax Reports</h1>
          <p className="mt-1 text-muted-brand">UK Self Assessment and Making Tax Digital record-keeping</p>
        </div>

        {/* Disclaimer Banner */}
        <Card className="border-amber-200 bg-amber-50 mb-6" data-testid="disclaimer-banner">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Record-Keeping Summary Only</p>
                <p className="text-sm text-amber-700 mt-1">
                  This report supports Self Assessment and MTD record-keeping. It is <strong>not</strong> a submission to HMRC.
                  Finance/mortgage costs are shown as plain expense totals without the finance-cost tax credit restriction.
                  Figures should be reviewed with a qualified accountant before filing.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Controls Row */}
        <Card className="border-line mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Tax Year Selector */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-brand" />
                <Select value={taxYear} onValueChange={setTaxYear}>
                  <SelectTrigger className="w-36" data-testid="tax-year-select">
                    <SelectValue placeholder="Tax Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_YEAR_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Period Toggle */}
              <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="period-toggle">
                <button
                  onClick={() => setPeriodType('full_year')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    periodType === 'full_year'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="period-full-year"
                >
                  Full Tax Year
                </button>
                <button
                  onClick={() => setPeriodType('quarter')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    periodType === 'quarter'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid="period-quarter"
                >
                  By Quarter
                </button>
              </div>

              {/* Quarter Selector (shown only when "By Quarter" is selected) */}
              {periodType === 'quarter' && (
                <Select value={quarter.toString()} onValueChange={(v) => setQuarter(parseInt(v))}>
                  <SelectTrigger className="w-48" data-testid="quarter-select">
                    <SelectValue placeholder="Select Quarter" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUARTER_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Property Filter */}
              <PropertyFilter
                selectedIds={selectedPropertyIds}
                onSelectionChange={setSelectedPropertyIds}
              />

              {/* Export Buttons */}
              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="outline"
                  onClick={exportCSV}
                  disabled={!report || loading || exporting}
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  onClick={exportPDF}
                  disabled={!report || loading || exporting}
                  className="bg-anchor-blue hover:bg-anchor-blue/90"
                  data-testid="export-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Completeness Warning */}
        {report?.has_any_data_gaps && (
          <Card className="border-orange-200 bg-orange-50 mb-6" data-testid="data-gaps-warning">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-orange-800">Incomplete Data Warning</p>
                  <p className="text-sm text-orange-700 mt-1">
                    One or more properties have missing monthly data within the selected period.
                    Totals may be understated. Missing months are indicated per property below.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <Card className="border-line">
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-anchor-blue animate-spin" />
            </CardContent>
          </Card>
        )}

        {/* No Properties Selected */}
        {!loading && selectedPropertyIds.length === 0 && (
          <Card className="border-line">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="w-16 h-16 text-line mb-4" />
              <h3 className="text-lg font-semibold text-ink font-display">No Properties Selected</h3>
              <p className="text-sm text-muted-brand mt-1 text-center max-w-md">
                Select one or more properties using the filter above to generate your tax report.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Report Content */}
        {!loading && report && selectedPropertyIds.length > 0 && (
          <div className="space-y-6">
            {/* Portfolio Summary */}
            <Card className="border-line" data-testid="portfolio-summary">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
                    <FileBarChart className="w-5 h-5 text-anchor-blue" />
                    Portfolio Summary
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {report.period_type === 'quarter' ? `Q${report.quarter}` : 'Full Year'} {report.tax_year}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Total Income */}
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-700">Total Rental Income</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700 font-mono" data-testid="total-income">
                      {formatCurrency(report.portfolio_totals.rental_income)}
                    </p>
                  </div>

                  {/* Total Expenses */}
                  <div className="bg-red-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      <span className="text-sm text-red-700">Total Expenses</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600 font-mono" data-testid="total-expenses">
                      {formatCurrency(report.portfolio_totals.total_expenses)}
                    </p>
                  </div>

                  {/* Net Profit */}
                  <div className={`rounded-xl p-4 ${report.portfolio_totals.net_profit >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <FileBarChart className={`w-4 h-4 ${report.portfolio_totals.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
                      <span className={`text-sm ${report.portfolio_totals.net_profit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                        Net Profit
                      </span>
                    </div>
                    <p className={`text-2xl font-bold font-mono ${report.portfolio_totals.net_profit >= 0 ? 'text-blue-700' : 'text-orange-700'}`} data-testid="net-profit">
                      {formatCurrency(report.portfolio_totals.net_profit)}
                    </p>
                  </div>
                </div>

                {/* Expense Category Breakdown */}
                <div className="border-t border-line pt-4">
                  <h4 className="text-sm font-medium text-muted-brand mb-3">Expense Breakdown by Category</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="expense-breakdown">
                    {EXPENSE_CATEGORIES.map(cat => (
                      <div key={cat.key} className="bg-cloud rounded-lg p-3">
                        <p className="text-xs text-muted-brand">{cat.label}</p>
                        <p className={`text-sm font-semibold font-mono ${cat.color}`}>
                          {formatCurrency(report.portfolio_totals[cat.key] || 0)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Property Breakdown */}
            <Card className="border-line" data-testid="property-breakdown">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-muted-brand" />
                  Property Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="property-table">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="text-left text-xs font-semibold text-muted-brand uppercase tracking-wider py-3 px-4">Property</th>
                        <th className="text-right text-xs font-semibold text-muted-brand uppercase tracking-wider py-3 px-4">Income</th>
                        <th className="text-right text-xs font-semibold text-muted-brand uppercase tracking-wider py-3 px-4">Expenses</th>
                        <th className="text-right text-xs font-semibold text-muted-brand uppercase tracking-wider py-3 px-4">Net Profit</th>
                        <th className="text-left text-xs font-semibold text-muted-brand uppercase tracking-wider py-3 px-4">Data Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.properties.map((prop, index) => (
                        <tr 
                          key={prop.property_id}
                          className={`border-b border-line last:border-0 ${index % 2 === 0 ? 'bg-white' : 'bg-cloud/50'}`}
                          data-testid={`property-row-${prop.property_id}`}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-muted-brand flex-shrink-0" />
                              <span className="text-sm font-medium text-ink">{prop.property_address}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className="text-sm font-semibold text-emerald-600 font-mono">
                              {formatCurrency(prop.rental_income)}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className="text-sm font-semibold text-red-600 font-mono">
                              {formatCurrency(prop.total_expenses)}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className={`text-sm font-bold font-mono ${prop.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                              {formatCurrency(prop.net_profit)}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            {prop.has_data_gaps ? (
                              <div>
                                <Badge 
                                  variant="outline" 
                                  className="bg-amber-100 text-amber-700 border-amber-200 text-xs"
                                  data-testid={`data-gap-badge-${prop.property_id}`}
                                >
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Missing Data
                                </Badge>
                                <p className="text-xs text-muted-brand mt-1">
                                  {prop.missing_months.slice(0, 3).join(', ')}
                                  {prop.missing_months.length > 3 && ` +${prop.missing_months.length - 3} more`}
                                </p>
                              </div>
                            ) : (
                              <Badge 
                                variant="outline" 
                                className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs"
                              >
                                Complete
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100">
                        <td className="py-4 px-4 font-semibold text-ink">Portfolio Total</td>
                        <td className="py-4 px-4 text-right">
                          <span className="text-sm font-bold text-emerald-600 font-mono">
                            {formatCurrency(report.portfolio_totals.rental_income)}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="text-sm font-bold text-red-600 font-mono">
                            {formatCurrency(report.portfolio_totals.total_expenses)}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className={`text-sm font-bold font-mono ${report.portfolio_totals.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                            {formatCurrency(report.portfolio_totals.net_profit)}
                          </span>
                        </td>
                        <td className="py-4 px-4"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Expense Breakdown by Property */}
            <Card className="border-line" data-testid="detailed-expense-breakdown">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold font-display">Detailed Expense Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="text-left text-xs font-semibold text-muted-brand uppercase tracking-wider py-3 px-3">Property</th>
                        {EXPENSE_CATEGORIES.map(cat => (
                          <th key={cat.key} className="text-right text-xs font-semibold text-muted-brand uppercase tracking-wider py-3 px-3">
                            {cat.label}
                          </th>
                        ))}
                        <th className="text-right text-xs font-semibold text-muted-brand uppercase tracking-wider py-3 px-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.properties.map((prop, index) => (
                        <tr 
                          key={prop.property_id}
                          className={`border-b border-line last:border-0 ${index % 2 === 0 ? 'bg-white' : 'bg-cloud/50'}`}
                        >
                          <td className="py-3 px-3 font-medium text-ink truncate max-w-[200px]" title={prop.property_address}>
                            {prop.property_address.split(',')[0]}
                          </td>
                          <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(prop.finance_mortgage)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(prop.letting_agent_fees)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(prop.insurance)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(prop.maintenance_repairs)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(prop.council_tax)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(prop.other_expenses)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs font-semibold text-red-600">
                            {formatCurrency(prop.total_expenses)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100 font-semibold">
                        <td className="py-3 px-3">Total</td>
                        <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(report.portfolio_totals.finance_mortgage)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(report.portfolio_totals.letting_agent_fees)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(report.portfolio_totals.insurance)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(report.portfolio_totals.maintenance_repairs)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(report.portfolio_totals.council_tax)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(report.portfolio_totals.other_expenses)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-red-600">
                          {formatCurrency(report.portfolio_totals.total_expenses)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
